import { useDefaultColumns, type Column, type ColumnDef, type CreditBlotterExposure } from '@talos/kyoko';
import { getAgGridColId } from '@talos/kyoko/src/components/BlotterTable/columns/getAgGridColId';
import { useMemo } from 'react';
import { getCreditUsageColumn } from './creditUsageColumn';

export function useCreditColumns(defaultColumns: Partial<Column>[] = []) {
  const columnDefinitions = useMemo(
    () =>
      new Map(
        (
          [
            {
              id: 'creditUsageWarning',
              type: 'creditUsageWarning',
              field: 'creditUsageWarning',
              aggregate: true,
              sortable: true,
              exportable: false,
            },
            {
              title: 'Market Account',
              field: 'MarketAccount',
              type: 'account',
              sortable: true,
              initialHide: true,
              params: {
                prefixCustomerMarketAccountsWithCpty: true,
              },
            },
            {
              field: 'Currency',
              type: 'currency',
              title: 'Asset',
              params: {
                colorful: true,
              },
            },
            {
              type: 'size',
              id: 'exposureAgg',
              title: 'Exposure',
              initialWidth: 225,
              field: 'effectiveExposureWithSign',
              params: {
                highlightNegative: true,
                currencyField: 'ExposureCurrency',
              },
            },
            {
              type: 'size',
              field: 'effectiveRemainingCredit',
              title: 'Remaining Credit',
              params: {
                highlightNegative: true,
                currencyField: 'ExposureCurrency',
              },
            },
            {
              type: 'size',
              id: 'exposureLimitAgg',
              field: 'effectiveLimitWithSign',
              title: 'NOP Limit',
              initialWidth: 225,
              params: {
                highlightNegative: true,
                currencyField: 'ExposureCurrency',
              },
              initialHide: true,
            },
            {
              type: 'size',
              id: 'shortLimit',
              field: 'short.limit',
              title: 'Short Limit',
              initialWidth: 225,
              params: {
                highlightNegative: true,
                currencyField: 'ExposureCurrency',
              },
              initialHide: true,
            },
            { type: 'exposureDefinition', field: 'ExposureDefinition', initialWidth: 180 },
            {
              type: 'size',
              id: 'longLimit',
              field: 'long.limit',
              title: 'Long Limit',
              initialWidth: 225,
              params: {
                highlightNegative: true,
                currencyField: 'ExposureCurrency',
              },
              initialHide: true,
            },
            getCreditUsageColumn<CreditBlotterExposure>({ colId: 'creditUsage', getCreditBlotterExposure: e => e }),
            {
              field: 'effectiveSide.talosExposure',
              type: 'size',
              title: 'NOP (Talos)',
              initialWidth: 225,
              initialHide: true,
              params: { highlightNegative: true, currencyField: 'ExposureCurrency' },
            },
            {
              field: 'effectiveSide.talosExposureLimit',
              type: 'size',
              title: 'NOP Limit (Talos)',
              initialWidth: 225,
              initialHide: true,
              params: { currencyField: 'ExposureCurrency' },
            },
            {
              field: 'effectiveSide.counterpartyExposure',
              type: 'size',
              title: 'NOP (Counterparty)',
              initialWidth: 225,
              initialHide: true,
              params: { highlightNegative: true, currencyField: 'ExposureCurrency' },
            },
            {
              field: 'effectiveSide.counterpartyExposureLimit',
              type: 'size',
              title: 'NOP Limit (Counterparty)',
              initialWidth: 225,
              initialHide: true,
              params: { currencyField: 'ExposureCurrency' },
            },
            { type: 'date', field: 'timestamp', title: 'Updated at', initialHide: true },
            { field: 'text', type: 'text', initialWidth: 100, initialHide: true },
            { field: 'status', type: 'exposureStatus', initialHide: true },
          ] satisfies ColumnDef<CreditBlotterExposure>[] as Column[]
        ).map(column => [getAgGridColId(column), column])
      ),
    []
  );

  return useDefaultColumns(defaultColumns, columnDefinitions);
}
